/**
 * VRG-427 — the AUTOMATIC half of VRG-378's user-initiated bug reporting.
 *
 * `recoverFromRenderError` is called from `ErrorBoundary.componentDidCatch` and
 * must NEVER throw back into it. A failure here re-entering the boundary that
 * called it is an infinite loop that would take out the page's recovery path
 * along with the crash itself — see the task's AC7. Every branch below is
 * wrapped so a failure anywhere in this file degrades to "not reported, but the
 * user still gets sent somewhere safe" rather than compounding the crash.
 *
 * SCOPE, stated plainly rather than left ambiguous (per the task): this only
 * ever fires for RENDER errors — the ones `ErrorBoundary` can see at all.
 * Event-handler throws and unhandled promise rejections do not reach a React
 * error boundary and are NOT reported by this file. Deliberately out of scope:
 * neither has a "previous page" to recover to in the way a killed render tree
 * does (the rest of the page is usually still alive and usable), so the
 * redirect-and-tell-the-user flow this file implements does not fit them.
 *
 * ## Revised, after shipping
 *
 * Two changes to the behaviour described above, both deliberate:
 *
 *  1. **Nothing is reported from a developer's machine.** See `isLocalDevelopment`.
 *     Recovery still runs and the console still carries the error; only the report
 *     and the modal are suppressed.
 *  2. **The report belongs to Vera by default and to the user by choice.** It is
 *     always filed — the stack trace is worth having regardless — but it surfaces
 *     in the user's own support list only once they add words to it in the crash
 *     modal. This REVERSES the original AC5. The reasoning lives in the API, at
 *     `features/support/support-visibility.ts`, since that is where it is enforced.
 */
import { supportApi } from 'services'
import { ROUTES } from 'routes'
import { getBuildContext, pageUrlFor } from './buildContext'
import { storage } from './storage'
import { getLastKnownGoodRoute } from './routeHistory'
import { createReportBudget } from './reportBudget'
import {
  apiFailureFromError,
  buildToastErrorContext,
  errorSignatureKey,
  shouldAutoReport,
  type ToastErrorContext,
} from './errorContext'

/** Read by `ErrorReportedModal` on the page the user is redirected BACK to. */
export const AUTO_ERROR_REPORTED_FLAG_KEY = 'vera:autoErrorReported'

/**
 * Is this a developer's machine rather than a deployment?
 *
 * ## Why this gate exists
 *
 * Reporting is unconditional today, so a render error thrown by an in-flight edit
 * files a REAL support request against the developer's own account — indistinguishable,
 * in the queue and in the notification, from a brand hitting a real crash. During a
 * single session of concurrent agent work this filed several. The signal-to-noise
 * cost is the whole argument: a bug queue that fills with a developer's half-saved
 * component stops being read, and the reports that matter are the ones that get lost.
 *
 * ## Why `import.meta.env.DEV` and not `VITE_ENVIRONMENT`
 *
 * `VITE_ENVIRONMENT` is the app's usual environment string and it is the WRONG axis
 * here. It is a build ARG (`apps/platform/Dockerfile`), and the legacy pipeline sets
 * it to `"development"` for the DEPLOYED dev environment (`bitbucket-pipelines.yml`)
 * — exactly the environment this gate must not silence. Gating on it would mean one
 * restored build arg silently turns off crash reporting for dev.vera.inc, and nothing
 * would report that it had.
 *
 * `import.meta.env.DEV` is Vite's own boolean and answers a different, harder-to-
 * misconfigure question: is this bundle being served by the dev server, or was it
 * produced by `vite build`? Every deployment runs `vite build` (both Docker images do),
 * so this is `false` on dev and production alike and `true` only where someone is
 * running `vite serve` — which is the actual definition of "localhost" for this app.
 * It cannot be flipped by an env var, only by changing how the app is built.
 *
 * ## The hostname clause
 *
 * A secondary, not a substitute. `vite build && vite preview` locally would give
 * `DEV === false`, and a preview build of the app on a laptop is still a developer's
 * machine. Loopback hostnames are never a deployment, so this is safe to OR in; it is
 * deliberately not the primary check, because a hostname alone would say nothing about
 * a bundle served from a tunnel or a LAN address.
 */
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '::1'])

export const isLocalDevelopment = (): boolean => {
  try {
    if (import.meta.env.DEV) return true
    return LOOPBACK_HOSTS.has(window.location.hostname)
  } catch {
    // Reached from inside the error path, so a throw here would defeat the whole
    // point. Unknown environment falls back to "not local" — reporting a crash we
    // did not need is a smaller failure than silencing a real one.
    return false
  }
}

/**
 * Hard caps so a component stuck in a genuine render loop cannot flood the
 * network before the redirect below unloads it. Server-side dedupe (AC3) stops
 * duplicate ROWS; this stops duplicate REQUESTS — the two are different
 * problems (see the task's "watch out" list).
 *
 * VRG-721: ONE budget for every automatic path — render crashes, error toasts,
 * `window.onerror`, `unhandledrejection` — so the caps are shared rather than
 * multiplied. See `reportBudget.ts`.
 */
/** How long to give the report a head start on the network before navigating away regardless. */
const REPORT_RACE_TIMEOUT_MS = 1200

// Module-level, not sessionStorage: this only needs to survive re-renders within
// the current tab's JS context, and the hard-reload recovery redirect below
// naturally resets it — which is exactly when a fresh budget is correct.
const budget = createReportBudget()

const wait = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms))

/**
 * A cheap CLIENT-side key, deliberately simpler than the server's normalised
 * signature — this only has to be stable enough to rate-limit a tight loop
 * within one tab, not to dedupe across users/sessions/versions (that is the
 * server's job, done properly, in `SupportService.computeErrorSignature`).
 */
const clientDedupeKey = (message: string, route: string): string =>
  `${message.slice(0, 200).toLowerCase()}::${route.split('?')[0]}`

/**
 * Fire the report if this occurrence is within budget.
 *
 * Returns `attempted: false` (never rejects) when the session cap or the
 * per-signature cooldown suppresses the call — the caller uses this to decide
 * whether it is honest to tell the user "this has been reported".
 */
/**
 * What the crash modal needs to exist at all, carried across the hard redirect.
 *
 * `reportId` is what makes the modal's optional note possible: the note is
 * attached to the report that was ALREADY filed, and the modal renders on a fresh
 * JS context after a full page load, so the id has to travel in storage or not at
 * all. It is optional because the report can still be in flight when the redirect
 * fires (see the race below) — in that case the modal tells the user it was
 * reported, which is true, and simply offers no field, because there is nothing
 * to attach words to.
 */
export interface AutoErrorReportedFlag {
  reportId?: string
}

const fileAutomaticReport = (
  error: Error,
  componentStack: string | null,
  crashedPath: string,
): { attempted: boolean; result: Promise<{ id: string } | undefined> } => {
  const message = error?.message || 'Unknown error'
  const key = clientDedupeKey(message, crashedPath)
  const now = Date.now()

  // `resolve(undefined)`, spelled out: `result` is now typed as resolving to the
  // filed report, and a suppressed call has no report. The caller reads `attempted`
  // and never awaits these, but the type has to say so.
  if (!budget.tryConsume(key, { now })) {
    return { attempted: false, result: Promise.resolve(undefined) }
  }

  const build = getBuildContext()

  return {
    attempted: true,
    // Rejections are absorbed HERE rather than at the call site, so `result` is a
    // promise that always settles and the caller can read the report id from it
    // without a second catch. A failed report is indistinguishable from a slow one
    // downstream, which is correct: both mean "no id to attach a note to".
    result: supportApi.reportAutomaticError({
      errorMessage: message,
      componentStack,
      pageUrl: pageUrlFor(crashedPath),
      environment: build.environment,
      appVersion: build.appVersion,
      clientContext: { ...build.clientContext, reportKind: 'render' },
    })
      .then(report => (report?.id ? { id: report.id } : undefined))
      .catch(() => undefined),
  }
}

/**
 * Report the crash (best-effort) and send the user back to safety. Called from
 * `ErrorBoundary.componentDidCatch`. Every branch ends in exactly one redirect —
 * this function is the recovery path, not just the reporting one.
 */
export const recoverFromRenderError = (error: Error, componentStack: string | null): void => {
  try {
    const crashedPath = `${window.location.pathname}${window.location.search}`
    const previous = getLastKnownGoodRoute()
    // Never redirect to the page that just crashed (nothing recorded yet, or the
    // "previous" route happens to equal it) — that would be sending the user
    // right back into the same failure.
    const redirectPath = previous && previous !== crashedPath ? previous : ROUTES.HOME

    // A hard navigation, not a router push: the crash may have left redux /
    // react-query / component state in an inconsistent shape, and this is the
    // one recovery that is guaranteed to reset all of it, including this
    // ErrorBoundary's own `hasError` state (which has no other way to clear —
    // the boundary sits ABOVE the router and would otherwise keep rendering its
    // fallback even after a client-side navigation to a healthy route).
    const finish = (reported: boolean, reportId?: string) => {
      try {
        if (reported) {
          // JSON, not the old `'1'`. The modal needs the report id to offer its
          // optional note, and a bare truthy flag cannot carry one. `'1'` is still
          // accepted on read (see `ErrorReportedModal`) so a tab mid-navigation
          // during a deploy does not get a modal-shaped crash of its own.
          const payload: AutoErrorReportedFlag = reportId ? { reportId } : {}
          sessionStorage.setItem(AUTO_ERROR_REPORTED_FLAG_KEY, JSON.stringify(payload))
        }
      } catch {
        /* sessionStorage unavailable (private mode, etc) — the redirect still happens */
      }
      try {
        window.location.assign(redirectPath)
      } catch {
        /* nothing more we can do from here */
      }
    }

    /*
      LOCAL DEVELOPMENT — suppress the REPORT and the MODAL, keep everything else.

      Deliberately placed after `finish` is defined and before any network call, so
      the recovery half runs untouched: the user (a developer, here) is still sent
      back to the last good route, the boundary's state is still cleared by the hard
      navigation, and `ErrorBoundary.componentDidCatch` has ALREADY written the error,
      its stack and the component stack to the console before calling this. A local
      crash must still fail loudly — silencing it would take the signal away from
      exactly the person who can fix it.

      The warning below is not decoration: without it the only visible difference
      between "reported" and "suppressed" is a modal that did not appear, and a
      developer who does not know about this gate would reasonably read that as the
      reporting being broken.
    */
    if (isLocalDevelopment()) {
      try {
        console.warn(
          '[errorReporter] Local development: this crash was NOT reported to support and no modal will be shown. ' +
            'The error itself is logged above. Reporting is active on dev and production.',
        )
      } catch {
        /* a console that throws is not worth failing recovery over */
      }
      finish(false)
      return
    }

    // Unauthenticated pages (sign-in, sign-up) have no session to attribute a
    // report to. Firing an authenticated-only endpoint there would also trip the
    // API client's own 401 -> refresh -> logout handling in the middle of crash
    // recovery, which is a worse outcome than simply not reporting. Deliberately
    // out of scope: the report is attributed to a user and a company, and an
    // anonymous crash on the sign-in page has neither. (This held under the
    // original AC5, which required the report to land in the user's own list, and
    // it still holds under the revision, which requires a user to CLAIM it.)
    if (!storage.getItem('accessToken')) {
      finish(false)
      return
    }

    const { attempted, result } = fileAutomaticReport(error, componentStack, crashedPath)
    if (!attempted) {
      // Rate-limited this occurrence — do not claim a report exists when this
      // specific call was suppressed.
      finish(false)
      return
    }

    // Best-effort delivery: give the request a head start before the hard
    // navigation below unloads the page (an in-flight XHR is not guaranteed to
    // survive that), but never block recovery on the network indefinitely.
    //
    // The race's VALUE now matters, not just its completion. `result` resolves to
    // the filed report (or `undefined` if it failed — it never rejects, see
    // `fileAutomaticReport`), while `wait` resolves to `undefined`, so whichever
    // wins hands `finish` either an id or nothing. That is precisely the right
    // semantics: if the network did not come back in time we cannot offer to
    // attach a note to a report we cannot name.
    Promise.race([result, wait(REPORT_RACE_TIMEOUT_MS).then(() => undefined)])
      .then(report => finish(true, report?.id))
      .catch(() => finish(true))
  } catch {
    // Absolute last resort — still get the user off the dead page even if
    // everything above somehow threw.
    try {
      window.location.assign(ROUTES.HOME)
    } catch {
      /* truly nothing more we can do */
    }
  }
}


// ─────────────────────────────────────────────────────────────────────────────
// VRG-721 — errors a render boundary never sees
// ─────────────────────────────────────────────────────────────────────────────
//
// The file header above scoped automatic reporting to RENDER errors and said
// event-handler throws and unhandled rejections were "deliberately out of scope"
// because they have no previous page to recover to. That reasoning was about the
// RECOVERY (redirect + modal), and it still holds: nothing below redirects, and
// nothing below opens a modal. What changed is the REPORT. The commonest failure a
// brand actually meets is not a crash but an error toast — a request that failed
// while the page stayed up — and none of those reached support unless the user
// chose to write in, which the VRG-719 product-create incident showed they mostly
// do not (five failed creates, zero reports).
//
// So these three paths file the same system report through the same endpoint,
// under the same budget and the same local/signed-out gates, and stop there.

/** Same gates as the render path: never from a developer's machine, never signed out. */
const canReport = (): boolean => {
  try {
    if (isLocalDevelopment()) return false
    return Boolean(storage.getItem('accessToken'))
  } catch {
    return false
  }
}

/**
 * signature key -> the automatic report it filed. Read by the support form so a
 * person who opens "Report a problem" from the same toast links their request to
 * the report Vera already has (`clientContext.autoReportId`). A promise, because
 * the user can click before the report's response arrives.
 */
const autoReportIds = new Map<string, Promise<string | undefined>>()

export const autoReportIdFor = (signatureKey: string): Promise<string | undefined> =>
  autoReportIds.get(signatureKey) ?? Promise.resolve(undefined)

const send = (params: {
  kind: 'toast' | 'uncaught' | 'rejection'
  message: string
  stack: string | null
  error: ToastErrorContext | null
  signatureKey: string
}): Promise<string | undefined> => {
  const build = getBuildContext()
  const page = params.error?.page ?? (typeof window !== 'undefined' ? window.location.pathname : null)
  const result = supportApi
    .reportAutomaticError({
      errorMessage: params.message.slice(0, 500),
      componentStack: params.stack,
      kind: params.kind,
      pageUrl: pageUrlFor(page),
      environment: build.environment,
      appVersion: build.appVersion,
      clientContext: {
        ...build.clientContext,
        reportKind: params.kind,
        ...(params.error ? { error: params.error } : {}),
      },
    })
    .then(report => report?.id)
    .catch(() => undefined)
  autoReportIds.set(params.signatureKey, result)
  return result
}

/**
 * The quiet report for an error toast. Called by `SnackbarProvider` for every
 * error toast it opens; decides for itself whether this one qualifies
 * (`shouldAutoReport`) and whether the budget allows it. Never throws.
 */
export const reportShownError = (
  ctx: ToastErrorContext,
  facts: { hasFieldErrors?: boolean; messageWasTechnical?: boolean; explicitClientError?: boolean },
): void => {
  try {
    if (!shouldAutoReport(ctx, facts) || !canReport()) return
    const signatureKey = errorSignatureKey(ctx)
    if (!budget.tryConsume(signatureKey, { once: true })) return
    void send({
      kind: 'toast',
      message: [ctx.title, ctx.text].filter(Boolean).join(' · ') || 'Error toast',
      stack: null,
      error: ctx,
      signatureKey,
    })
  } catch {
    /* reporting is best-effort and must never break the toast that called it */
  }
}

/**
 * Noise every browser produces that is not a defect in this app: the
 * ResizeObserver loop warning, the opaque "Script error." a cross-origin script
 * (an extension, a third-party widget) is reduced to, and anything thrown from an
 * extension's own URL.
 */
const IGNORED_GLOBAL_MESSAGES = [/ResizeObserver loop/i, /^Script error\.?$/i]
const EXTENSION_SOURCE = /^(chrome|moz|safari)(-web)?-extension:\/\//i

const reportGlobal = (
  kind: 'uncaught' | 'rejection',
  message: string,
  stack: string | null,
  source: string | null,
): void => {
  try {
    if (!message || IGNORED_GLOBAL_MESSAGES.some(re => re.test(message))) return
    if (source && EXTENSION_SOURCE.test(source)) return
    if (stack && EXTENSION_SOURCE.test(stack.split('\n').find(l => l.includes('://')) ?? '')) return
    if (!canReport()) return
    const route = typeof window !== 'undefined' ? window.location.pathname : ''
    const signatureKey = `${kind} ${clientDedupeKey(message, route)}`
    if (!budget.tryConsume(signatureKey, { once: true })) return
    void send({ kind, message, stack: stack ? stack.slice(0, 8000) : null, error: null, signatureKey })
  } catch {
    /* never throw from a global error handler */
  }
}

let globalHandlersInstalled = false

/**
 * `window.onerror` + `unhandledrejection`, feeding the same reporter.
 *
 * Installed once, from `index.tsx`, with `addEventListener` rather than by
 * assigning `window.onerror`, so a third-party script that also assigns it
 * cannot silently replace ours (or be replaced by us).
 *
 * An unhandled rejection that is an API error is classified by the SAME rule as
 * a toast (`shouldAutoReport`) — an expected 404 nobody caught is not a bug
 * report, a 500 nobody caught is. Anything else is reported as its message.
 */
const classifyApiRejection = (
  reason: unknown,
): { ctx: ToastErrorContext; hasFieldErrors: boolean } | null => {
  const failure = apiFailureFromError(reason)
  if (!failure) return null
  return {
    ctx: buildToastErrorContext({
      title: null,
      text: null,
      failure,
      attribution: 'explicit',
      page: typeof window !== 'undefined' ? window.location.pathname : null,
    }),
    hasFieldErrors: failure.hasFieldErrors,
  }
}

export const installGlobalErrorHandlers = (): void => {
  if (globalHandlersInstalled || typeof window === 'undefined') return
  globalHandlersInstalled = true

  window.addEventListener('error', event => {
    const error = (event as ErrorEvent).error
    const message =
      (error instanceof Error ? error.message : null) || (event as ErrorEvent).message || ''
    reportGlobal(
      'uncaught',
      message,
      error instanceof Error ? error.stack ?? null : null,
      (event as ErrorEvent).filename || null,
    )
  })

  window.addEventListener('unhandledrejection', event => {
    const reason = (event as PromiseRejectionEvent).reason
    try {
      const api = classifyApiRejection(reason)
      if (api) {
        if (!shouldAutoReport(api.ctx, { hasFieldErrors: api.hasFieldErrors, messageWasTechnical: true })) return
        if (!canReport()) return
        const signatureKey = errorSignatureKey(api.ctx)
        if (!budget.tryConsume(signatureKey, { once: true })) return
        void send({
          kind: 'rejection',
          message: `Unhandled request failure: ${[api.ctx.method, api.ctx.path, api.ctx.status].filter(Boolean).join(' ')}`,
          stack: null,
          error: api.ctx,
          signatureKey,
        })
        return
      }
    } catch {
      /* fall through to the generic report */
    }
    const message =
      reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : 'Unhandled promise rejection'
    reportGlobal('rejection', message, reason instanceof Error ? reason.stack ?? null : null, null)
  })
}
